import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import styles from './Chart.module.css';
import { calculateYearlyData } from '../../utils/calculations';
import { useTranslation } from 'react-i18next';

function InvestmentChart({ data, currencySymbol }) {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const yearlyData = calculateYearlyData(data);
    const periods = yearlyData.map(d => d.period);

    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: periods,
        datasets: [
          {
            label: t('chart.interest'),
            data: yearlyData.map(d => d.interest),
            backgroundColor: 'rgba(82, 145, 255, 0.5)',
          },
          {
            label: t('chart.contributions'),
            data: yearlyData.map(d => d.contributions),
            backgroundColor: 'rgb(82, 145, 255)',
          },
          {
            label: t('chart.initialDeposit'),
            data: Array(periods.length).fill(data.initialDeposit),
            backgroundColor: 'rgb(25, 101, 255)',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: true,
            title: {
              display: true,
              text: data.timeUnit === 'years' ? t('chart.year') : t('chart.month')
            }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            title: {
              display: true,
              text: t('chart.balance')
            },
            ticks: {
              callback: function(value) {
                return currencySymbol + value.toLocaleString();
              }
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: function(tooltipItems) {
                const period = tooltipItems[0].label;
                return `${data.timeUnit === 'years' ? t('chart.year') : t('chart.month')} ${period}`;
              },
              afterBody: function(tooltipItems) {
                const index = tooltipItems[0].dataIndex;
                const periodData = yearlyData[index];
                return [
                  `${t('chart.totalBalance')}: ${currencySymbol}${periodData.balance.toLocaleString()}`,
                  `${t('chart.totalInterest')}: ${currencySymbol}${periodData.interest.toLocaleString()}`,
                  `${t('chart.totalContributions')}: ${currencySymbol}${periodData.contributions.toLocaleString()}`
                ];
              },
              label: function() {
                return '';
              }
            },
            displayColors: false,
            padding: 12,
            titleFont: {
              weight: 'bold'
            }
          }
        }
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, currencySymbol, t]);

  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartWrapper}>
        <canvas ref={chartRef}></canvas>
      </div>
    </div>
  );
}

export default InvestmentChart; 